import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Ban, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePagination } from '@/hooks/use-pagination';
import { formatDate } from '@/lib/format';
import { useMarketingSettings, useSuppress, useSuppressions, useUnsuppress } from './api';
import { CHANNEL_LABELS, SUPPRESSION_REASON_LABELS } from './labels';

const TABS = [
  ['suppression', 'Suppression list'],
  ['settings', 'Sending rules'],
] as const;

export function MarketingSettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'suppression';

  return (
    <div>
      <PageHeader
        title="Marketing setup"
        description="Who must never be contacted, and the limits every send is held to."
      />

      <div className="mb-5 flex gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'suppression' ? <SuppressionList /> : <SendingRules />}
    </div>
  );
}

function SuppressionList() {
  const pagination = usePagination();
  const [channel, setChannel] = useState('');
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useSuppressions({
    page: pagination.page,
    channel: channel || undefined,
    reason: reason || undefined,
    q: search || undefined,
  });
  const unsuppress = useUnsuppress();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Ban className="h-4 w-4 text-destructive" />
            The list nothing may send to
          </CardTitle>
          <CardDescription>
            Checked before every send on every channel. No campaign, journey or setting can
            override it — an unsubscribe a campaign can ignore is not an unsubscribe.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-9 max-w-xs"
          placeholder="Search an address or number…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
        />
        <Select
          className="h-9 w-auto"
          value={channel}
          onChange={(event) => {
            setChannel(event.target.value);
            pagination.reset();
          }}
        >
          <option value="">All channels</option>
          {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          className="h-9 w-auto min-w-[11rem]"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            pagination.reset();
          }}
        >
          <option value="">Any reason</option>
          {Object.entries(SUPPRESSION_REASON_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Can permission="marketing.campaign.update">
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add someone
          </Button>
        </Can>
      </div>

      {isLoading ? (
        <TableSkeleton columns={4} />
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Why</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      Nobody is suppressed.
                    </TableCell>
                  </TableRow>
                ) : (
                  (data?.data ?? []).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="text-sm font-medium">{row.value}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {CHANNEL_LABELS[row.channel]}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {SUPPRESSION_REASON_LABELS[row.reason] ?? row.reason}
                        </Badge>
                        {row.source && (
                          <div className="mt-0.5 text-xs text-muted-foreground">{row.source}</div>
                        )}
                        {row.softBounceCount > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {row.softBounceCount} soft bounces counted
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Can permission="marketing.campaign.update">
                          <button
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => unsuppress.mutate(row.id)}
                            title="Remove from the list"
                            aria-label="Remove"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Removing someone who unsubscribed themselves puts them back in scope for marketing. It is
        written to the audit log, and it should be rare.
      </p>

      {adding && <AddDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const suppress = useSuppress();
  const [channel, setChannel] = useState('EMAIL');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');

  return (
    <Card className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md shadow-brand-lg sm:inset-x-auto sm:right-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Add to the suppression list</CardTitle>
        <CardDescription>They will never receive marketing on this channel again.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
          <div className="space-y-1.5">
            <Label>Channel</Label>
            <Select value={channel} onChange={(event) => setChannel(event.target.value)}>
              {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{channel === 'EMAIL' ? 'Email address' : 'Phone number'}</Label>
            <Input value={value} onChange={(event) => setValue(event.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Note</Label>
          <Input value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={value.length < 3 || suppress.isPending}
            onClick={async () => {
              await suppress.mutateAsync({ channel, value, reason: 'DO_NOT_CONTACT', note });
              onClose();
            }}
          >
            Add
          </Button>
          <Button size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SendingRules() {
  const { data: settings, isLoading } = useMarketingSettings();

  if (isLoading || !settings) return <TableSkeleton columns={2} />;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">What every email carries</CardTitle>
          <CardDescription>
            An unsubscribe link and a postal address are added to every marketing email, and
            neither can be switched off — they are what separate marketing email from spam, in the
            law and in the filters.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row label="From name" value={settings.fromName ?? 'Not set'} />
          <Row label="From address" value={settings.fromEmail ?? 'Not set'} bad={!settings.fromEmail} />
          <Row label="Reply to" value={settings.replyTo ?? 'The from address'} />
          <div className="border-t pt-3">
            <div className="text-muted-foreground">Postal address</div>
            {settings.postalAddress ? (
              <p className="mt-0.5 whitespace-pre-line font-medium">{settings.postalAddress}</p>
            ) : (
              <div className="mt-1 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                <span>
                  No postal address is set, so every campaign will fail its pre-send check. Add
                  one to the organisation record.
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Limits
          </CardTitle>
          <CardDescription>
            Applied across campaigns and journeys together, so an automated sequence cannot
            quietly double someone&rsquo;s share.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row
            label="Marketing messages per contact"
            value={`${settings.frequencyCapPerWeek} a week`}
          />
          <Row
            label="Soft bounces before suppression"
            value={String(settings.softBounceLimit)}
          />
          <div className="border-t pt-3">
            <div className="mb-1.5 text-muted-foreground">WhatsApp opt-out keywords</div>
            <div className="flex flex-wrap gap-1.5">
              {settings.optOutKeywords.map((keyword) => (
                <Badge key={keyword} variant="outline">
                  {keyword}
                </Badge>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              An inbound message matching one of these suppresses the number immediately, before
              it is even filed.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={bad ? 'font-medium text-destructive' : 'font-medium'}>{value}</span>
    </div>
  );
}
